export type OverrideMissionWarningOptions = {
  isShown: boolean;
  missionId: string | null;
  vehicleId: string | null;
  vehicleName: string | null;
  vehicleTypeId: string | null;
};

/**
 * Filters missions data based on search value
 * @param missions - Array of missions to filter
 * @param searchValue - Search term to filter by
 * @returns Filtered array of missions
 */
export const filterMissionsDataBySearch = (
  missions: Mission[],
  searchValue: string
): Mission[] => {
  if (!searchValue) return missions;

  const lowerSearch = searchValue.toLowerCase();
  return missions.filter((mission) =>
    mission.name?.toLowerCase().includes(lowerSearch)
  );
};

/**
 * Finds a vehicle by ID
 * @param vehicles - Array of vehicles
 * @param vehicleId - ID of the vehicle to find
 * @returns Vehicle object or undefined
 */
export const findVehicleById = (
  vehicles: Vehicle[],
  vehicleId: string
): Vehicle | undefined => {
  return vehicles.find((vehicle) => vehicle?.id === vehicleId);
};

/**
 * Checks if a vehicle is in sync state
 * @param vehiclesSyncStateById - Record of vehicle sync states
 * @param vehicleId - ID of the vehicle to check
 * @returns Boolean indicating if vehicle is syncing
 */
export const isVehicleSyncing = (
  vehiclesSyncStateById: Record<string, boolean>,
  vehicleId: string
): boolean => {
  return Boolean(vehiclesSyncStateById[vehicleId]);
};

/**
 * Creates override mission warning options
 * @param missionId - ID of the mission
 * @param vehicle - Vehicle object
 * @returns Override mission warning options
 */
export const createOverrideMissionWarningOptions = (
  missionId: string,
  vehicle: Vehicle | undefined
): OverrideMissionWarningOptions => {
  return {
    isShown: true,
    missionId: missionId || null,
    vehicleId: vehicle?.id || null,
    vehicleName: vehicle?.name || null,
    vehicleTypeId: vehicle?.vehicleTypeId || null,
  };
};

/**
 * Resets override mission warning options
 * @returns Reset override mission warning options
 */
export const resetOverrideMissionWarningOptions =
  (): OverrideMissionWarningOptions => {
    return {
      isShown: false,
      missionId: null,
      vehicleId: null,
      vehicleName: null,
      vehicleTypeId: null,
    };
  };

/**
 * Validates if mission data is complete (for Mission type)
 * @param mission - Mission object to validate
 * @returns Boolean indicating if mission is valid
 */
export const isValidMissionData = (mission: Mission): boolean => {
  return Boolean(mission.id && mission.name && (mission as any).status);
};

/**
 * Validates if override mission warning options are complete
 * @param options - Override mission warning options
 * @returns Boolean indicating if options are valid for override
 */
export const canOverrideMission = (
  options: OverrideMissionWarningOptions
): boolean => {
  return Boolean(
    options.vehicleId && options.vehicleName && options.vehicleTypeId
  );
};

/**
 * Creates mission cancellation data
 * @param missionId - ID of the mission to cancel
 * @param vehicleId - ID of the vehicle
 * @returns Mission cancellation data
 */
export const createMissionCancellationData = (
  missionId: string,
  vehicleId: string
): { missionId: string; vehicleId: string } => {
  return {
    missionId,
    vehicleId,
  };
};

/**
 * Gets the deployment date for a mission with fallback to timeStamp
 * @param mission - Mission object
 * @returns Deployment date string or null
 */
export const getMissionDeploymentDate = (mission: Mission): string | null => {
  return mission?.deployedAt || mission?.timeStamp || null;
};

/**
 * Sorts missions by deployment date in descending order (most recent first)
 * @param missions - Array of missions to sort
 * @returns Sorted array of missions
 */
export const sortMissionsByDeploymentDate = (
  missions: Mission[]
): Mission[] => {
  return missions.sort((a, b) => {
    const aDeployedAt = getMissionDeploymentDate(a);
    const bDeployedAt = getMissionDeploymentDate(b);

    // Handle cases where dates might be null/undefined
    if (!aDeployedAt && !bDeployedAt) return 0;
    if (!aDeployedAt) return 1; // Put missions without deployedAt at the end
    if (!bDeployedAt) return -1;

    // Convert to Date objects and compare
    const dateA = new Date(aDeployedAt);
    const dateB = new Date(bDeployedAt);

    // Descending order (most recent first)
    return dateB.getTime() - dateA.getTime();
  });
};

/**
 * Counts total missions by status (for Mission type)
 * @param missions - Array of missions
 * @returns Record with status counts
 */
export const countMissionsByStatusData = (
  missions: Mission[]
): Record<string, number> => {
  return missions.reduce((acc, mission) => {
    const status = mission.status;
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
};
